import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';

/**
 * <DutyHolderSuggestions /> — link buildings to their duty holder from what's
 * already typed on Related Companies.
 *
 * Buildings set up before the Finance tab existed have their companies only as
 * names, so their invoices go out with no accounts email, reference or c/o address.
 * This proposes the links. It never applies one silently, and it won't promote the
 * wrong company: a "c/o …" name is the managing agent, and when an invoice fund is
 * named, the instructing client is linked alongside it rather than as duty holder.
 *
 * Pass a building to scope it to that one (the Finance tab); omit for all.
 */
const JSON_HEADERS = { 'Content-Type': 'application/json' };

const DutyHolderSuggestions = ({ propertyId = null, localPropertyId = null, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const scoped = !!(propertyId || localPropertyId);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = propertyId ? `?property_id=${propertyId}` : localPropertyId ? `?local_property_id=${localPropertyId}` : '';
      const d = await apiFetch(`/api/contractor/duty-holder-suggestions${qs}`);
      setRows(Array.isArray(d?.suggestions) ? d.suggestions : []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [propertyId, localPropertyId]);
  useEffect(() => { load(); }, [load]);

  const target = (b) => (b.local_property_id ? { local_property_id: b.local_property_id } : { property_id: b.property_id });

  /** Link a client to a building, optionally marking it the duty holder. */
  const linkClient = async (b, clientId, asDutyHolder) => {
    await apiFetch(`/api/contractor/clients/${clientId}/properties`, {
      method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(target(b)),
    });
    if (!asDutyHolder) return;
    // The query string goes AFTER the path. Building it as `${id}?local=1` and then
    // appending /duty-holder put it mid-path, so for contractor-only buildings the
    // lookup failed and the new client was linked but never made duty holder.
    const pid = b.local_property_id || b.property_id;
    const d = await apiFetch(`/api/contractor/properties/${pid}/duty-holder${b.local_property_id ? '?local=1' : ''}`);
    if (!d?.linked) throw new Error('Could not confirm the link');
    const link = d.linked.find((l) => String(l.client_id) === String(clientId));
    if (!link) throw new Error('Linked, but could not mark it as the duty holder');
    await apiFetch(`/api/contractor/clients/${clientId}/properties/${link.link_id}/primary`, { method: 'PUT' });
  };

  const accept = async (b, m) => {
    setBusy(`${b.name}|${m.client_id}`);
    try {
      await linkClient(b, m.client_id, m.as_duty_holder);
      toast.success(m.as_duty_holder ? `${m.client_name} is now the duty holder for ${b.name}` : `${m.client_name} linked to ${b.name}`);
      load(); onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, 'Could not link')); }
    finally { setBusy(null); }
  };

  /** The fund is named but has no client record: create it and make it duty holder. */
  const createDutyHolder = async (b) => {
    setBusy(`${b.name}|new`);
    try {
      const c = await apiFetch('/api/contractor/clients', {
        method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ name: b.missing_duty_holder }),
      });
      const id = c?.id ?? c?.client?.id;
      if (!id) throw new Error('no id');
      await linkClient(b, id, true);
      toast.success(`Created ${b.missing_duty_holder} as the duty holder for ${b.name} — add its accounts email and c/o address on its Billing tab`);
      load(); onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, 'Could not create the client')); }
    finally { setBusy(null); }
  };

  if (loading) return null;
  if (!rows.length) {
    return scoped ? null : (
      <div style={{ padding: '18px', fontSize: '0.84rem', color: 'var(--cw-text-muted)' }}>
        Nothing to suggest — every building whose Related Companies name a client is already linked.
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid #fde68a', background: '#fffdf5', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #fde68a', fontSize: '0.74rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#92400e' }}>
        <i className="fa-solid fa-wand-magic-sparkles" /> Suggested from Related Companies
      </div>
      {rows.map((b) => (
        <div key={b.local_property_id ? `l${b.local_property_id}` : `p${b.property_id}`} style={{ padding: '12px 16px', borderTop: '1px solid #fef3c7' }}>
          {!scoped && <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--cw-text)', marginBottom: 6 }}>{b.name}</div>}

          {b.missing_duty_holder && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ flex: '1 1 260px', fontSize: '0.82rem', color: 'var(--cw-text-2)' }}>
                <strong>{b.missing_duty_holder}</strong> is named as the invoice fund, but there's no client record for it yet —
                so nothing can be invoiced to it properly.
              </span>
              <button className="cw-btn cw-btn-primary" style={{ fontSize: '0.76rem' }} disabled={!!busy} onClick={() => createDutyHolder(b)}>
                <i className={`fa-solid ${busy === `${b.name}|new` ? 'fa-spinner fa-spin' : 'fa-plus'}`} /> Create as duty holder
              </button>
            </div>
          )}

          {b.matches.map((m) => (
            <div key={m.client_id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', padding: '6px 0' }}>
              <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                <div style={{ fontSize: '0.84rem', color: 'var(--cw-text)' }}>
                  <strong>{m.client_name}</strong>
                  <span style={{ color: 'var(--cw-text-muted)' }}> · named as {m.role_label}</span>
                </div>
                {m.caution && <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: 2 }}><i className="fa-solid fa-circle-exclamation" /> {m.caution}</div>}
              </div>
              <button className={`cw-btn ${m.as_duty_holder ? 'cw-btn-primary' : 'cw-btn-secondary'}`} style={{ fontSize: '0.76rem' }}
                disabled={!!busy} onClick={() => accept(b, m)}>
                <i className={`fa-solid ${busy === `${b.name}|${m.client_id}` ? 'fa-spinner fa-spin' : 'fa-link'}`} />
                {m.as_duty_holder ? ' Link as duty holder' : ' Link alongside'}
              </button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default DutyHolderSuggestions;
